import { SnapshotOrInstance, getRoot, types, type Instance } from "@gadgetinc/mobx-quick-tree";
import { EnvironmentConfig } from "../../EnvironmentConfig";
import { ShopifyBasePartnerConfig } from "./ShopifyBasePartnerConfig";
import { ShopifyPartnerAppConfig } from "./ShopifyPartnerAppConfig";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
export const ShopifyPartnerConnectionConfig = ShopifyBasePartnerConfig("ShopifyPartnerConnectionConfig", ShopifyPartnerAppConfig)
  .props({
    environmentConfigs: types.map(types.reference(EnvironmentConfig)),
  })
  .views((self) => ({
    get environmentConfigsAsArray(): Instance<typeof EnvironmentConfig>[] {
      return Array.from(self.environmentConfigs.values());
    },
  }))
  .views((self) => ({
    get appConfigs() {
      const appConfigValues = self.environmentConfigsAsArray.flatMap((ec) =>
        ShopifyPartnerAppConfig.is(ec.configuration) ? [ec.configuration] : []
      );
      return new Map(appConfigValues.map((ac) => [ac.key, ac]));
    },
  }))
  .views((self) => ({
    get appConfigsAsArray(): Instance<typeof ShopifyPartnerAppConfig>[] {
      return Array.from(self.appConfigs.values());
    },
  }))
  .actions((self) => ({
    putEnvironmentConfig(config: Instance<typeof EnvironmentConfig>): Instance<typeof EnvironmentConfig> {
      self.environmentConfigs.put(config);

      return config;
    },
    putAppConfig(config: SnapshotOrInstance<typeof ShopifyPartnerAppConfig>): Instance<typeof ShopifyPartnerAppConfig> {
      const root = getRoot(self);
      const environmentConfig = root.addNewEnvironmentConfig({ configuration: config });
      self.environmentConfigs.put(environmentConfig);

      return environmentConfig.configuration;
    },
    removeAppConfig(configKey: string): boolean {
      const environmentConfig = self.environmentConfigsAsArray.find((ec) => ec.configuration.key === configKey);

      if (!environmentConfig) {
        throw new Error(`No environment config found for app config ${configKey}`);
      }

      return self.environmentConfigs.delete(environmentConfig.key);
    },
    removeEnvironmentConfig(configKey: string): boolean {
      return self.environmentConfigs.delete(configKey);
    },
  }));
